/**
 * The folders of a department of memory (D340 phase 3): the trie of the source files its items came from, with chains of
 * single-child folders folded into one ("src/memory"), each folder knowing how many items, files and Markdown files lie
 * under it. No DOM.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md), brain/_core.js
 * `rebuildStructure` / `expandDir`: a directory is a node with `n.files` under it, drawn as a bubble with the count in it,
 * whose children are listed only when it is expanded. Ours reads the path of each memory item's source file; the source
 * reads the folders of a vault.
 */

/** A path as the folders are told apart by: forward slashes, no leading `./`. */
export function normalisePath(raw: string): string {
  return raw.replace(/\\/g, '/').replace(/^\.\//, '').replace(/\/{2,}/g, '/');
}

const isAbsolutePath = (p: string) => p.startsWith('/') || /^[A-Za-z]:\//.test(p);

/**
 * The paths with what they all share at the front taken off, when they are absolute (a graph that names files by their full
 * path would otherwise hang every folder from the same ten levels of `/home/…`). Relative paths are left as they are.
 */
export function relativePaths(paths: ReadonlyArray<string | undefined>): Array<string | undefined> {
  const norm = paths.map((p) => (p ? normalisePath(p) : undefined));
  const abs = norm.filter((p): p is string => !!p && isAbsolutePath(p));
  if (!abs.length) return norm;
  const dirs = abs.map((p) => p.split('/').slice(0, -1));
  let common = dirs[0];
  for (const d of dirs) {
    let i = 0;
    while (i < common.length && i < d.length && common[i] === d[i]) i++;
    common = common.slice(0, i);
  }
  const cut = common.length;
  return norm.map((p) => (p && isAbsolutePath(p) ? p.split('/').slice(cut).join('/') : p));
}

export interface FolderCount {
  /** Items (graph nodes) under the folder, however deep. */
  items: number;
  /** Distinct source files they came from. */
  files: number;
  /** How many of those files are Markdown. */
  markdown: number;
}

/** One folder: its full path, the name it is drawn with and what is directly in it. */
export interface Folder {
  /** The whole path from the department's top ("src/memory"). */
  path: string;
  /** The name it is drawn with: one segment, or several joined when a folder held only another. */
  name: string;
  /** The folders directly inside it. */
  folders: Folder[];
  /** The item ids directly inside it. */
  direct: string[];
  count: FolderCount;
}

export interface FolderTree {
  /** The items with no folder (a file at the top, or no file at all). */
  loose: string[];
  /** The department's top folders. */
  folders: Folder[];
}

interface Raw {
  name: string;
  path: string;
  kids: Map<string, Raw>;
  direct: string[];
  sources: Set<string>;
}

const raw = (name: string, path: string): Raw => ({ name, path, kids: new Map(), direct: [], sources: new Set() });

/** Count what lies under a raw folder, and turn it into a `Folder`, folding a chain of single-child folders into its last. */
function seal(r: Raw): Folder {
  let node = r;
  let name = r.name;
  while (node.direct.length === 0 && node.kids.size === 1) {
    node = [...node.kids.values()][0];
    name = `${name}/${node.name}`;
  }
  const folders = [...node.kids.values()].map(seal).sort((a, b) => b.count.items - a.count.items || a.name.localeCompare(b.name, undefined, { numeric: true }));
  const sources = new Set<string>();
  const collect = (x: Raw) => {
    for (const s of x.sources) sources.add(s);
    for (const k of x.kids.values()) collect(k);
  };
  collect(node);
  const items = node.direct.length + folders.reduce((n, f) => n + f.count.items, 0);
  return {
    path: node.path,
    name,
    folders,
    direct: node.direct,
    count: { items, files: sources.size, markdown: [...sources].filter((s) => /\.(md|mdx|markdown)$/i.test(s)).length },
  };
}

/** The folders of some items, each given by its id and the path of the source file it came from (none: loose). */
export function folderTree(entries: ReadonlyArray<{ id: string; path?: string }>): FolderTree {
  const root = raw('', '');
  const loose: string[] = [];
  for (const e of entries) {
    const p = e.path ? normalisePath(e.path) : '';
    const parts = p.split('/').filter(Boolean);
    if (parts.length < 2) {
      loose.push(e.id);
      continue;
    }
    let at = root;
    for (const seg of parts.slice(0, -1)) {
      let next = at.kids.get(seg);
      if (!next) {
        next = raw(seg, at.path ? `${at.path}/${seg}` : seg);
        at.kids.set(seg, next);
      }
      at = next;
    }
    at.direct.push(e.id);
    at.sources.add(p);
  }
  const folders = [...root.kids.values()].map(seal).sort((a, b) => b.count.items - a.count.items || a.name.localeCompare(b.name, undefined, { numeric: true }));
  return { loose, folders };
}
